import { Decimal } from "decimal.js";
import { getCurrentServer, getCurrentServerKey } from "@/utils/globalData";

const DISPLAY_CURRENCY_META_MAP = Object.freeze({
  CNY: {
    flagIcon: "/flags/currency-cny.svg",
    symbol: "¥",
    decimals: 2,
    locale: "zh-CN",
    showCodeWhenAmbiguous: true,
  },
  JPY: {
    flagIcon: "/flags/currency-jpy.svg",
    symbol: "¥",
    decimals: 0,
    locale: "ja-JP",
    showCodeWhenAmbiguous: true,
  },
  USD: {
    flagIcon: "/flags/currency-usd.svg",
    symbol: "$",
    decimals: 2,
    locale: "en-US",
    showCodeWhenAmbiguous: false,
  },
  TWD: {
    flagIcon: "/flags/currency-twd.svg",
    symbol: "NT$",
    decimals: 0,
    locale: "zh-TW",
    showCodeWhenAmbiguous: false,
  },
  AUD: {
    flagIcon: "/flags/currency-aud.svg",
    symbol: "AU$",
    decimals: 2,
    locale: "en-AU",
    showCodeWhenAmbiguous: false,
  },
});

const DISPLAY_LOCALE_MAP = Object.freeze({
  zh: "zh-CN",
  "zh-CN": "zh-CN",
  zh_CN: "zh-CN",
  "zh-TW": "zh-TW",
  zh_TW: "zh-TW",
  tw: "zh-TW",
  en: "en-US",
  "en-US": "en-US",
  en_US: "en-US",
  "en-GB": "en-GB",
  en_GB: "en-GB",
  ja: "ja-JP",
  "ja-JP": "ja-JP",
  ja_JP: "ja-JP",
  vi: "vi-VN",
  "vi-VN": "vi-VN",
  vi_VN: "vi-VN",
});

const DEFAULT_CURRENCY_META = Object.freeze({
  flagIcon: "/flags/currency-default.svg",
  symbol: "$",
  decimals: 2,
  locale: "en-US",
  showCodeWhenAmbiguous: false,
});

const ambiguousCurrencySymbolSet = (() => {
  const symbolCodeCountMap = new Map();

  Object.entries(DISPLAY_CURRENCY_META_MAP).forEach(([, meta]) => {
    const symbol = String(meta.symbol || "").trim();
    if (!symbol) {
      return;
    }

    symbolCodeCountMap.set(symbol, (symbolCodeCountMap.get(symbol) || 0) + 1);
  });

  return new Set(
    Array.from(symbolCodeCountMap.entries())
      .filter(([, count]) => count > 1)
      .map(([symbol]) => symbol)
  );
})();

export function normalizeCurrencyCode(currencyCode = "") {
  return String(currencyCode || "")
    .trim()
    .toUpperCase();
}

export function normalizeDisplayLocale(locale = "") {
  const normalizedLocale = String(locale || "").trim();
  return DISPLAY_LOCALE_MAP[normalizedLocale] || normalizedLocale || "en-US";
}

export function getCurrencyMeta(currencyCode = "") {
  const normalizedCurrencyCode = normalizeCurrencyCode(currencyCode) || "USD";
  const matchedMeta = DISPLAY_CURRENCY_META_MAP[normalizedCurrencyCode] || {};
  const resolvedMeta = {
    ...DEFAULT_CURRENCY_META,
    ...matchedMeta,
    code: normalizedCurrencyCode,
  };

  return {
    ...resolvedMeta,
    isAmbiguousSymbol: ambiguousCurrencySymbolSet.has(resolvedMeta.symbol),
  };
}

export function getCurrencySymbol(currencyCode = "", options = {}) {
  const { withCodeWhenAmbiguous = false } = options;
  const currencyMeta = getCurrencyMeta(currencyCode);

  if (withCodeWhenAmbiguous && currencyMeta.isAmbiguousSymbol) {
    return `${currencyMeta.symbol} ${currencyMeta.code}`;
  }

  return currencyMeta.symbol;
}

export function getCurrencyFlag(currencyCode = "") {
  return getCurrencyMeta(currencyCode).flagIcon;
}

function normalizeCurrencyRateItem(item = {}) {
  const fromCurrencyCode = normalizeCurrencyCode(item?.fromCurrencyCode);
  const toCurrencyCode = normalizeCurrencyCode(item?.toCurrencyCode);
  const exchangeRate = Number(item?.exchangeRate || 0);

  if (
    !fromCurrencyCode ||
    !toCurrencyCode ||
    !Number.isFinite(exchangeRate) ||
    exchangeRate <= 0
  ) {
    return null;
  }

  return {
    ...item,
    fromCurrencyCode,
    toCurrencyCode,
    exchangeRate,
  };
}

export function buildDisplayCurrencyGraph(currencyList = [], currentCurrency = null) {
  const graph = new Map();
  const addEdge = (fromCurrencyCode, toCurrencyCode, exchangeRate) => {
    if (!fromCurrencyCode || !toCurrencyCode) {
      return;
    }

    const normalizedExchangeRate = Number(exchangeRate);
    if (!Number.isFinite(normalizedExchangeRate) || normalizedExchangeRate <= 0) {
      return;
    }

    if (!graph.has(fromCurrencyCode)) {
      graph.set(fromCurrencyCode, new Map());
    }

    graph.get(fromCurrencyCode).set(toCurrencyCode, normalizedExchangeRate);
  };

  const normalizedCurrencyList = Array.isArray(currencyList)
    ? currencyList
        .map((item) => normalizeCurrencyRateItem(item))
        .filter(Boolean)
    : [];

  normalizedCurrencyList.forEach((item) => {
    addEdge(item.fromCurrencyCode, item.toCurrencyCode, item.exchangeRate);
    addEdge(item.toCurrencyCode, item.fromCurrencyCode, 1 / item.exchangeRate);
  });

  const normalizedCurrentCurrency = normalizeCurrencyRateItem(currentCurrency || {});
  if (normalizedCurrentCurrency) {
    addEdge(
      normalizedCurrentCurrency.fromCurrencyCode,
      normalizedCurrentCurrency.toCurrencyCode,
      normalizedCurrentCurrency.exchangeRate
    );
    addEdge(
      normalizedCurrentCurrency.toCurrencyCode,
      normalizedCurrentCurrency.fromCurrencyCode,
      1 / normalizedCurrentCurrency.exchangeRate
    );
  }

  return graph;
}

export function resolveDisplayRate(
  currencyList = [],
  fromCurrencyCode = "",
  toCurrencyCode = "",
  currentCurrency = null
) {
  const normalizedFromCurrencyCode = normalizeCurrencyCode(fromCurrencyCode);
  const normalizedToCurrencyCode = normalizeCurrencyCode(toCurrencyCode);

  if (!normalizedFromCurrencyCode || !normalizedToCurrencyCode) {
    return 0;
  }

  if (normalizedFromCurrencyCode === normalizedToCurrencyCode) {
    return 1;
  }

  const currencyGraph = buildDisplayCurrencyGraph(currencyList, currentCurrency);
  const visitedCurrencySet = new Set([normalizedFromCurrencyCode]);
  const pendingCurrencyQueue = [[normalizedFromCurrencyCode, 1]];

  while (pendingCurrencyQueue.length) {
    const [currentCurrencyCode, currentRate] = pendingCurrencyQueue.shift();
    const connectedCurrencyMap = currencyGraph.get(currentCurrencyCode);

    if (!connectedCurrencyMap) {
      continue;
    }

    for (const [nextCurrencyCode, nextRate] of connectedCurrencyMap.entries()) {
      if (visitedCurrencySet.has(nextCurrencyCode)) {
        continue;
      }

      const resolvedRate = Number(
        new Decimal(currentRate || 0).times(nextRate || 0).toNumber()
      );

      if (nextCurrencyCode === normalizedToCurrencyCode) {
        return resolvedRate;
      }

      visitedCurrencySet.add(nextCurrencyCode);
      pendingCurrencyQueue.push([nextCurrencyCode, resolvedRate]);
    }
  }

  return 0;
}

export function convertDisplayAmount(amount, options = {}) {
  const {
    fromCurrencyCode = "",
    toCurrencyCode = "",
    currentCurrency = null,
    currencyList = [],
  } = options;
  const normalizedAmount = Number(amount);
  const normalizedFromCurrencyCode = normalizeCurrencyCode(fromCurrencyCode);
  const normalizedToCurrencyCode = normalizeCurrencyCode(toCurrencyCode);

  if (!Number.isFinite(normalizedAmount)) {
    return 0;
  }

  if (
    !normalizedFromCurrencyCode ||
    !normalizedToCurrencyCode ||
    normalizedFromCurrencyCode === normalizedToCurrencyCode
  ) {
    return normalizedAmount;
  }

  const resolvedRate = resolveDisplayRate(
    currencyList,
    normalizedFromCurrencyCode,
    normalizedToCurrencyCode,
    currentCurrency
  );

  if (!Number.isFinite(resolvedRate) || resolvedRate <= 0) {
    return normalizedAmount;
  }

  return Number(new Decimal(normalizedAmount).times(resolvedRate).toNumber());
}

export function formatDisplayMoney(amount, options = {}) {
  const {
    currencyCode = "",
    locale = "",
    decimals,
    withCode = false,
    trimTrailingZeros = false,
    spaceBetweenSymbolAndAmount = false,
  } = options;
  const currencyMeta = getCurrencyMeta(currencyCode);
  const normalizedAmount = Number(amount);
  const resolvedAmount = Number.isFinite(normalizedAmount) ? normalizedAmount : 0;
  const resolvedLocale = normalizeDisplayLocale(locale || currencyMeta.locale);
  const resolvedDecimals = currencyMeta.code === "TWD" ? 0 : Number.isFinite(Number(decimals))
    ? Math.max(0, Number(decimals))
    : currencyMeta.decimals;
  const formattedAmount = resolvedAmount.toLocaleString(resolvedLocale, {
    minimumFractionDigits: trimTrailingZeros ? 0 : resolvedDecimals,
    maximumFractionDigits: resolvedDecimals,
  });
  const symbol = getCurrencySymbol(currencyMeta.code);
  const moneyText = spaceBetweenSymbolAndAmount
    ? `${symbol} ${formattedAmount}`
    : `${symbol}${formattedAmount}`;

  return withCode && currencyMeta.code !== "TWD" ? `${moneyText} ${currencyMeta.code}` : moneyText;
}

export function resolveDisplayMoneyDecimals(options = {}) {
  const {
    currencyCode = "",
    fromCurrencyCode = "",
    baseCurrencyCode = "",
    decimals,
  } = options;
  const normalizedCurrencyCode = normalizeCurrencyCode(currencyCode);
  const normalizedFromCurrencyCode = normalizeCurrencyCode(fromCurrencyCode);
  const normalizedBaseCurrencyCode = normalizeCurrencyCode(baseCurrencyCode);

  if (Number.isFinite(Number(decimals))) {
    return Math.max(0, Number(decimals));
  }

  if (
    normalizedCurrencyCode === "TWD"
    && normalizedFromCurrencyCode
    && normalizedFromCurrencyCode !== "TWD"
    && normalizedBaseCurrencyCode !== "TWD"
  ) {
    return 0;
  }

  return getCurrencyMeta(normalizedCurrencyCode).decimals;
}

export function formatDisplayRate(rate, options = {}) {
  const {
    locale = "",
    minimumFractionDigits = 0,
    maximumFractionDigits = 3,
  } = options;
  const normalizedRate = Number(rate);

  if (!Number.isFinite(normalizedRate)) {
    return "--";
  }

  const roundedRate = Number(normalizedRate.toFixed(maximumFractionDigits));
  return roundedRate.toLocaleString(normalizeDisplayLocale(locale), {
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

export function getCurrentCurrencyStorageKey(currentServer = getCurrentServer()) {
  return `currentCurrency:${getCurrentServerKey(currentServer)}`;
}

export function readStoredCurrentCurrency(currentServer = getCurrentServer()) {
  if (!process.client) {
    return null;
  }

  try {
    const currentCurrencyString =
      window.localStorage.getItem(getCurrentCurrencyStorageKey(currentServer)) ||
      window.localStorage.getItem("currentCurrency");
    const currentCurrency = JSON.parse(currentCurrencyString || "null");

    return currentCurrency && typeof currentCurrency === "object"
      ? currentCurrency
      : null;
  } catch (error) {
    console.log(error);
    return null;
  }
}
